import React, { useContext, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Slider,
	Switch,
	TextField,
	Typography,
	TextareaAutosize,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileItem from "./fileItem";
import { AuthContext } from "../context";

let filesContent = [];

// Rimozione delle interface TypeScript
export default function GenericFileInput(props) {
	const authContext = useContext(AuthContext);
	const [files, setFiles] = useState([]);
	const [originalFiles, setOriginalFiles] = useState([]);
	const [errors, setErrors] = useState({});
	const imageExtensions = [
		".jpg",
		".jpeg",
		".png",
		".gif",
		".bmp",
		".tiff",
		".tif",
		".webp",
		".heif",
		".heic",
		".svg",
		".ico",
		".psd",
		".raw",
		".ai",
		".eps",
	];

	const handleFileChange = async (event) => {
		if (!event.target.files) return;

		const newFiles = [...files];
		const filePromises = Array.from(event.target.files).map(
			(file) =>
				new Promise((resolve) => {
					const reader = new FileReader();
					reader.onloadend = () => {
						const fileObj = {
							file: {
								name: file.name,
								type: file.type,
								size: file.size,
								modified: file.lastModified,
								imageData: file,
							},
						};
						const contentObj = {
							content: reader.result,
						};

						filesContent.push(contentObj);
						props?.inputFields?.forEach((field) => {
							fileObj[field.name] =
								field.type === "checkbox" ||
								field.type === "switch"
									? false
									: "";
						});

						resolve(fileObj);
					};
					reader.readAsDataURL(file);
				})
		);

		const processedFiles = await Promise.all(filePromises);
		setFiles([...newFiles, ...processedFiles]);
	};

	const validateField = (index, field, value) => {
		const isRequired = field.required !== false;
		const error =
			isRequired &&
			(value === "" || value === undefined || value === null)
				? `${field.label} è obbligatorio`
				: "";
		setErrors((prevErrors) => ({
			...prevErrors,
			[`${index}-${field.name}`]: error,
		}));
	};

	const handleInputChange = (index, field, value) => {
		setFiles((prevFiles) =>
			prevFiles.map((fileInfo, i) =>
				i === index ? { ...fileInfo, [field]: value } : fileInfo
			)
		);
		const fieldConfig = props?.inputFields?.find((f) => f.name === field);
		if (fieldConfig) {
			validateField(index, fieldConfig, value);
		}
	};

	const validateAllFields = () => {
		const newErrors = {};
		files.forEach((fileInfo, index) => {
			props?.inputFields.forEach((field) => {
				const isRequired = field.required !== false;
				if (
					isRequired &&
					(fileInfo[field.name] === "" ||
						fileInfo[field.name] === undefined ||
						fileInfo[field.name] === null)
				) {
					newErrors[`${index}-${field.name}`] =
						`${field.label} è obbligatorio`;
				}
			});
		});
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		if (validateAllFields()) {
			let fileMerged = [];
			for (let i = 0; i < files.length; i++) {
				let fileApp = { ...files[i] };
				if (fileApp?.file) {
					fileApp.file.content = filesContent[i].content;
				}

				fileMerged.push(fileApp);
			}
			props?.onSubmit(fileMerged);
		} else {
			console.error("Please fill in all required fields for each file");
		}
	};

	React.useEffect(() => {
		if (props?.files) {
			setFiles(props?.files);
			setOriginalFiles(props?.files);
			filesContent = [];
			for (let i = 0; i < props.files.length; i++) {
				filesContent.push({ content: null });
			}
		}
	}, [props?.files]);

	const renderInput = (field, fileInfo, index) => {
		const value = fileInfo[field.name];
		const error = errors[`${index}-${field.name}`];

		switch (field.type) {
			case "text":
			case "number":
				return (
					<TextField
						fullWidth
						label={field.label}
						value={value}
						onChange={(e) =>
							handleInputChange(index, field.name, e.target.value)
						}
						error={!!error}
						helperText={error}
						type={field.type}
						InputProps={
							field.type === "number"
								? {
										inputProps: {
											min: field.min,
											max: field.max,
										},
									}
								: undefined
						}
						sx={{ width: "250px" }}
					/>
				);
			case "textArea":
				return (
					<>
						<TextareaAutosize
							placeholder={field.label}
							value={value}
							onChange={(e) =>
								handleInputChange(
									index,
									field.name,
									e.target.value
								)
							}
							minRows={field.minRows || 3}
							maxRows={field.maxRows || 5}
							style={{
								width: "100%",
								padding: "12px",
								borderRadius: "8px",
								border: "1px solid #ccc",
								backgroundColor: "#f9f9f9",
								fontSize: "16px",
								lineHeight: "1.5",
								transition: "border-color 0.3s ease",
							}}
							onFocus={(e) =>
								(e.target.style.borderColor = "#007BFF")
							}
							onBlur={(e) =>
								(e.target.style.borderColor = "#ccc")
							}
							readOnly={field.readOnly || false}
						/>
						{error && (
							<Typography
								variant="caption"
								color="error"
								sx={{
									display: "block",
									marginTop: "4px",
									fontSize: "14px",
								}}
							>
								{error}
							</Typography>
						)}
					</>
				);
			case "date":
				return (
					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale="it"
					>
						<DatePicker
							label={field.label}
							value={value ? dayjs(value) : null}
							onChange={(newValue) => {
								const formattedDate = newValue
									? dayjs(newValue).format("YYYY-MM-DD")
									: null;
								handleInputChange(
									index,
									field.name,
									formattedDate
								);
							}}
							sx={{
								"& .MuiOutlinedInput-root": {
									"& fieldset": {
										borderColor: !!error
											? "red"
											: "default",
									},
									"&:hover fieldset": {
										borderColor: !!error
											? "red"
											: "default",
									},
									"&.Mui-focused fieldset": {
										borderColor: !!error
											? "red"
											: "default",
									},
									width: "170px",
								},
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									name={field.name}
									error={!!error}
									helperText={error}
									sx={{ width: "500px" }}
									onChange={(e) => {
										const parsedDate = dayjs(
											e.target.value,
											"DD-MM-YYYY"
										);
										if (parsedDate.isValid()) {
											handleInputChange(
												field.name,
												parsedDate.format("DD-MM-YYYY")
											);
										}
									}}
								/>
							)}
							inputFormat="DD-MM-YYYY"
						/>
					</LocalizationProvider>
				);
			case "checkbox":
				return (
					<FormControlLabel
						control={
							<Checkbox
								checked={value}
								onChange={(e) =>
									handleInputChange(
										index,
										field.name,
										e.target.checked
									)
								}
							/>
						}
						label={field.label}
						sx={{ ml: 2 }}
					/>
				);
			case "radio":
				return (
					<FormControl component="fieldset" error={!!error}>
						<Typography variant="subtitle2">
							{field.label}
						</Typography>
						<RadioGroup
							value={value}
							onChange={(e) =>
								handleInputChange(
									index,
									field.name,
									e.target.value
								)
							}
						>
							{field.options?.map((option) => (
								<FormControlLabel
									key={option.value}
									value={option.value}
									control={<Radio />}
									label={option.label}
								/>
							))}
						</RadioGroup>
						{error && (
							<Typography variant="caption" color="error">
								{error}
							</Typography>
						)}
					</FormControl>
				);
			case "slider":
				return (
					<Box>
						<Typography variant="subtitle2">
							{field.label}
						</Typography>
						<Slider
							value={value || 0}
							onChange={(_, newValue) =>
								handleInputChange(index, field.name, newValue)
							}
							min={field.min}
							max={field.max}
							valueLabelDisplay="auto"
							sx={{ width: "150px" }}
						/>
						{error && (
							<Typography variant="caption" color="error">
								{error}
							</Typography>
						)}
					</Box>
				);
			case "switch":
				return (
					<FormControlLabel
						control={
							<Switch
								checked={value}
								onChange={(e) =>
									handleInputChange(
										index,
										field.name,
										e.target.checked
									)
								}
							/>
						}
						label={field.label}
					/>
				);
			case "select":
				return (
					<FormControl fullWidth error={!!error}>
						<InputLabel>{field.label}</InputLabel>
						<Select
							value={
								field.multiple
									? Array.isArray(value)
										? value
										: []
									: value || ""
							} // Imposta un array vuoto se multiple è true e value non è un array
							onChange={(e) =>
								handleInputChange(
									index,
									field.name,
									field.multiple
										? e.target.value
										: e.target.value // Passa l'array o il singolo valore
								)
							}
							label={field.label}
							multiple={!!field.multiple} // Rende la Select multipla se field.multiple è true
							sx={{ width: "250px", height: "50px" }}
						>
							{field.options?.map((option) => (
								<MenuItem
									key={option.value}
									value={option.value}
								>
									{option.label}
								</MenuItem>
							))}
						</Select>
						{error && (
							<Typography variant="caption" color="error">
								{error}
							</Typography>
						)}
					</FormControl>
				);

			case "readOnly":
				return (
					<TextField
						label={field.label}
						value={value}
						InputProps={{
							readOnly: true,
						}}
						sx={{ width: "250px", height: "50px" }}
					/>
				);

			default:
				return null;
		}
	};

	const handleRemoveFile = (index) => {
		if (authContext.hasAuth("DELETE_" + props?.authModelName)) {
			setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
			filesContent.slice(index, 1);
		}
	};

	const maxWidth = `${400 + 200 * props?.inputFields.length}px`;

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={{
				width: "100%",
				mx: "auto",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
			}}
		>
			{!props?.disableUploadFiles && (
				<>
					<input
						type="file"
						multiple
						onChange={handleFileChange}
						style={{ display: "none" }}
						id="file-input"
					/>
					<Box direction="row" gap={"large"}>
						<label htmlFor="file-input">
							<Button
								variant="contained"
								component="span"
								sx={{
									backgroundColor: "#333232",
									color: "white!important",
									mb: 2,
									width: "220px",
									height: "50px",
									fontSize: "16px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "8px",
								}}
							>
								<FileUploadIcon
									sx={{ transform: "scale(1.5)" }}
								/>{" "}
								{/* Puoi specificare qui la dimensione in px o em */}
								CARICA FILE
							</Button>
						</label>
						{JSON.stringify(files) !==
							JSON.stringify(originalFiles) &&
							authContext.hasAuth(
								"UPDATE_" + props?.authModelName
							) && (
								<Button
									type="submit"
									variant="contained"
									component="span"
									sx={{
										backgroundColor: "#514f4f",
										color: "white!important",
										mb: 2,
										width: "220px",
										height: "50px",
										fontSize: "16px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "8px",
									}}
									onClick={(e) => handleSubmit(e)}
								>
									SALVA
								</Button>
							)}
					</Box>
				</>
			)}
			{files.map((fileInfo, index) => (
				<FileItem
					index={index}
					fileInfo={fileInfo}
					handleRemoveFile={handleRemoveFile}
					props={{
						imageExtensions: imageExtensions,
						inputFields: props?.inputFields,
						renderInput: renderInput,
						authModelName: props?.authModelName,
					}}
					maxWidth={maxWidth}
				/>
			))}

			{JSON.stringify(files) !== JSON.stringify(originalFiles) &&
				authContext.hasAuth("UPDATE_" + props?.authModelName) && (
					<Button
						type="submit"
						variant="contained"
						component="span"
						sx={{
							backgroundColor: "#514f4f",
							color: "white!important",
							mb: 2,
							width: "220px",
							height: "50px",
							fontSize: "16px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "8px",
						}}
						onClick={(e) => handleSubmit(e)}
					>
						SALVA
					</Button>
				)}
		</Box>
	);
}
